import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";

import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { apiService } from "../../services/api";

const SkillForm = ({
  clubId,
  categories,
  ratingScales,
  editingSkill,
  onSuccess,
  onCancel,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    evaluation_criteria: "",
    category_id: "",
    rating_scale_id: "",
    is_required: true,
    weight: 1.0,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editingSkill?.id) {
      // Editing existing skill
      setFormData({
        name: editingSkill.name || "",
        description: editingSkill.description || "",
        evaluation_criteria: editingSkill.evaluation_criteria || "",
        category_id: editingSkill.category_id || "",
        rating_scale_id: editingSkill.rating_scale_id || "",
        is_required:
          editingSkill.is_required !== undefined
            ? editingSkill.is_required
            : true,
        weight: editingSkill.weight || 1.0,
      });
    } else if (editingSkill?.categoryId) {
      // Pre-select category if provided for new skill
      setFormData((prev) => ({
        ...prev,
        category_id: editingSkill.categoryId,
      }));
    }
  }, [editingSkill]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        club_id: clubId,
        weight: parseFloat(formData.weight),
      };

      if (editingSkill?.id) {
        await apiService.updateSkill(editingSkill.id, submitData);
        toast.success("Skill updated successfully!");
      } else {
        await apiService.createSkill(submitData);
        toast.success("Skill created successfully!");
      }

      onSuccess();
    } catch (error) {
      toast.error("Failed to save skill");
    }
    setLoading(false);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const selectedScale = ratingScales.find(
    (scale) => scale.id === formData.rating_scale_id,
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Input
        label="Skill Name"
        placeholder="e.g., Ground Ball Recovery, Passing Accuracy, Speed"
        value={formData.name}
        onChange={(e) => handleInputChange("name", e.target.value)}
        required
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Category *
          </label>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-slate-500 focus:border-slate-500"
            value={formData.category_id}
            onChange={(e) => handleInputChange("category_id", e.target.value)}
            required
          >
            <option value="">Select a category</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Rating Scale *
          </label>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-slate-500 focus:border-slate-500"
            value={formData.rating_scale_id}
            onChange={(e) =>
              handleInputChange("rating_scale_id", e.target.value)
            }
            required
          >
            <option value="">Select a rating scale</option>
            {ratingScales.map((scale) => (
              <option key={scale.id} value={scale.id}>
                {scale.name} ({scale.scale_type.replace("_", " ")})
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedScale && (
        <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
          <h4 className="text-sm font-medium text-slate-900 mb-1">
            Selected Rating Scale
          </h4>
          <p className="text-sm text-slate-800">
            {selectedScale.scale_type === "numeric" &&
              `Range: ${selectedScale.min_value}-${selectedScale.max_value} (±${selectedScale.increment})`}
            {selectedScale.scale_type === "pass_fail" && "Pass/Fail evaluation"}
            {selectedScale.scale_type === "custom_options" &&
              `Options: ${selectedScale.custom_options?.join(", ")}`}
          </p>
          {selectedScale.description && (
            <p className="text-sm text-slate-700 mt-1">
              {selectedScale.description}
            </p>
          )}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description (Optional)
        </label>
        <textarea
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-slate-500 focus:border-slate-500"
          rows="2"
          placeholder="Brief description of this skill..."
          value={formData.description}
          onChange={(e) => handleInputChange("description", e.target.value)}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Evaluation Criteria (Optional)
        </label>
        <textarea
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-slate-500 focus:border-slate-500"
          rows="3"
          placeholder="Detailed instructions for evaluators on how to score this skill..."
          value={formData.evaluation_criteria}
          onChange={(e) =>
            handleInputChange("evaluation_criteria", e.target.value)
          }
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Weight
          </label>
          <Input
            type="number"
            step="0.1"
            min="0.1"
            max="5.0"
            placeholder="1.0"
            value={formData.weight}
            onChange={(e) => handleInputChange("weight", e.target.value)}
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            1.0 = normal weight, 1.5 = 50% more important, 0.5 = half weight
          </p>
        </div>

        <div className="flex items-center pt-6">
          <input
            type="checkbox"
            id="is_required"
            checked={formData.is_required}
            onChange={(e) => handleInputChange("is_required", e.target.checked)}
            className="h-4 w-4 text-slate-600 focus:ring-slate-500 border-gray-300 rounded"
          />
          <label
            htmlFor="is_required"
            className="ml-2 block text-sm text-gray-700"
          >
            Required skill (must be scored for every player)
          </label>
        </div>
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={loading}>
          {loading
            ? "Saving..."
            : editingSkill?.id
              ? "Update Skill"
              : "Create Skill"}
        </Button>
      </div>
    </form>
  );
};

export default SkillForm;
